// Phase R3 — Slip Extractor Section B "Brief" (design refactor).
//
// Visual port of the Claude Design handoff `NegotiationBrief`
// (extractor-art0.jsx) onto the se-* token namespace: numbered
// 01/02/03 section stack, mode tabs, dropzone/paste, optional stake
// context, "Open the session" CTA.
//
// Contract-frozen: SlipExtractorFormProps + the full extraction
// state machine + every data-testid are preserved verbatim. Only the
// chrome changed (zero sa-* / Autopsy classes left in this file).
//
// Stake context (engineering ruling): an OPTIONAL per-session form
// input in v1 — never required, never pre-filled, never used to
// predict outcomes. Absent ⇒ the break-even anchor simply will not
// render downstream (N08). Embedded PlanCard / ResultPanel get their
// own chrome in R4 / R5.

import { useEffect, useState } from "react";

import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { computeCombinedOdds } from "../../extractor/filterExecutor.ts";
import { useSlipExtraction } from "../../extractor/useSlipExtraction.ts";
import {
  SlipUploadDropzone,
  type SlipUploadValue,
} from "../slip-autopsy-2/SlipUploadDropzone.tsx";
import { PlanCard } from "./PlanCard.tsx";
import { PromptComposer } from "./PromptComposer.tsx";
import { ResultPanel } from "./ResultPanel.tsx";
import { computeBreakEvenAnchor } from "../../extractor/nelFinancial.ts";
import { SE } from "./seTokens.ts";

export type ExtractorMode = "upload" | "paste";

/** Submit payload — SE-4 transforms this into the Edge-fn body. */
export interface ExtractorSubmitPayload {
  input_mode: ExtractorMode;
  slip_text: string;            // empty for upload mode
  slip_image_path: string | null;
  slip_image_mime: string | null;
  slip_image_size_bytes: number | null;
  slip_image_file_name: string | null;
  user_prompt: string;
  /** T3.2 — "optimise" (Starter+, default) or "stress_test" (the
   *  Sika Pro+ adversarial sensitivity analysis). */
  analysis: "optimise" | "stress_test";
  /** R3 — optional per-session stake context (powers the NEL
   *  break-even anchor at seal). null ⇒ anchor never renders (N08). */
  stake: number | null;
  /** R3 — optional weekly cadence, paired with stake for the
   *  "weeks covered" framing. */
  slips_per_week: number | null;
}

export interface SlipExtractorFormProps {
  /** Called when the user clicks Run. SE-3 wires console.log; SE-4
   * wires the hook + Edge-fn invoke. */
  onSubmit?: (payload: ExtractorSubmitPayload) => void;
  /** When true, the form locks (parent is mid-submit). */
  submitting?: boolean;
}

const MIN_PASTE_LEN = 10;
const MIN_PROMPT_LEN = 5;
const STAKE_OPTIONS = [50, 100, 200, 500, 1000];
const SPW_OPTIONS = [1, 3, 5, 7, 10];

// ─── Design primitives (local — se-* tokenised) ─────────────────

function SectionHead(
  { number, eyebrow, sub }: {
    number: string;
    eyebrow: string;
    sub?: string;
  },
): JSX.Element {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          fontFamily: SE.fontMono,
          fontSize: 11,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: SE.text,
        }}
      >
        <span style={{ color: SE.textDim }}>{number}</span>
        <span>{eyebrow}</span>
      </div>
      {sub && (
        <div
          style={{
            marginTop: 8,
            fontFamily: SE.fontBody,
            fontSize: 12.5,
            color: SE.textSec,
            lineHeight: 1.55,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function StakeField(
  {
    label,
    unit,
    value,
    onChange,
    options,
    testid,
  }: {
    label: string;
    unit: string;
    value: number | null;
    onChange: (v: number | null) => void;
    options: number[];
    testid: string;
  },
): JSX.Element {
  return (
    <div data-testid={testid} style={{ flex: 1, minWidth: 220 }}>
      <div
        style={{
          fontFamily: SE.fontMono,
          fontSize: 10,
          letterSpacing: "0.22em",
          color: SE.textMuted,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 4 }}>
        {options.map((o) => {
          const active = o === value;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(active ? null : o)}
              data-active={active || undefined}
              style={{
                flex: 1,
                padding: "10px 4px",
                background: active ? SE.text : "transparent",
                color: active ? SE.surfacePage : SE.textSec,
                border: `1px solid ${active ? SE.text : SE.border}`,
                fontFamily: SE.fontBody,
                fontSize: 12.5,
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                borderRadius: 2,
              }}
            >
              {unit === "×" ? `${o}${unit}` : `${unit}${o}`}
            </button>
          );
        })}
      </div>
      {value === null && (
        <div
          style={{
            marginTop: 8,
            fontFamily: SE.fontMono,
            fontSize: 9.5,
            letterSpacing: "0.04em",
            color: SE.textMuted,
          }}
        >
          None selected
        </div>
      )}
    </div>
  );
}

/** Inline progress card used by extracting / scoring / planning /
 *  executing branches. */
function ProgressCard(
  { testid, eyebrow, body }: {
    testid: string;
    eyebrow: string;
    body?: string;
  },
): JSX.Element {
  return (
    <div
      data-testid={testid}
      role="status"
      style={{
        padding: "24px 20px",
        border: `1px solid ${SE.borderBright}`,
        borderRadius: 4,
        background: SE.surfaceCard,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          fontFamily: SE.fontMono,
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: SE.amber,
        }}
      >
        {eyebrow}
      </div>
      {body && (
        <div
          style={{
            fontFamily: SE.fontBody,
            fontSize: 13.5,
            color: SE.textSec,
            lineHeight: 1.55,
          }}
        >
          {body}
        </div>
      )}
    </div>
  );
}

export function SlipExtractorForm({
  onSubmit,
  submitting: submittingProp,
}: SlipExtractorFormProps): JSX.Element {
  const { user } = useAuthSession();
  const {
    state: extractionState,
    result: extractionResult,
    filterPlan,
    filterResult,
    liabilities,
    analysisDegraded,
    degradedLegCount,
    stressReport,
    financialContext,
    inputError: extractionInputError,
    extract: runExtract,
    resumeExtraction,
    scoreAndPlan,
    cancelPlan,
    runPlan,
    reset: resetExtraction,
  } = useSlipExtraction();

  // Slice E — return hand-off from Slip Analysis: ?extraction=<id> loads the
  // swarm-judged legs straight into the review/prune step.
  useEffect(() => {
    const eid = new URLSearchParams(window.location.search).get("extraction");
    if (eid) void resumeExtraction(eid);
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { hasTier } = useUserTier();
  const canStressTest = hasTier("sika_pro");
  const [analysis, setAnalysis] = useState<"optimise" | "stress_test">(
    "optimise",
  );
  const [mode, setMode] = useState<ExtractorMode>("upload");
  const [pasteText, setPasteText] = useState("");
  const [upload, setUpload] = useState<SlipUploadValue | null>(null);
  const [prompt, setPrompt] = useState("");
  const [stake, setStake] = useState<number | null>(null);
  const [slipsPerWeek, setSlipsPerWeek] = useState<number | null>(null);
  // Editable copy of the extracted legs — the user corrects any OCR misreads
  // here before Score & plan. Re-seeded only when a NEW extraction completes.
  const [editedLegs, setEditedLegs] = useState<any[] | null>(null);

  useEffect(() => {
    if (extractionState === "extracted" && extractionResult) {
      setEditedLegs(extractionResult.extracted_legs.map((l) => ({ ...l })));
    } else if (extractionState === "idle" || extractionState === "error_input") {
      setEditedLegs(null);
    }
  }, [extractionState, extractionResult]);

  const submitting = submittingProp || extractionState === "extracting";

  // Submit gate (spec §7 SE-3 done criteria): slip present AND prompt ≥ 5
  const slipReady = mode === "upload"
    ? upload != null
    : pasteText.trim().length >= MIN_PASTE_LEN;
  const promptReady = prompt.trim().length >= MIN_PROMPT_LEN;
  const submitReady = slipReady && promptReady && !submitting;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!submitReady) return;
    const payload: ExtractorSubmitPayload = {
      input_mode: mode,
      slip_text: mode === "paste" ? pasteText.trim() : "",
      slip_image_path: mode === "upload" ? upload?.path ?? null : null,
      slip_image_mime: mode === "upload" ? upload?.mime ?? null : null,
      slip_image_size_bytes: mode === "upload"
        ? upload?.sizeBytes ?? null
        : null,
      slip_image_file_name: mode === "upload"
        ? upload?.fileName ?? null
        : null,
      user_prompt: prompt.trim(),
      // T3.2 — stress-test only when the user is Sika Pro+ (the
      // server independently re-checks; this is the UX gate).
      analysis: analysis === "stress_test" && canStressTest
        ? "stress_test"
        : "optimise",
      // R3 — optional, never required.
      stake,
      slips_per_week: slipsPerWeek,
    };
    if (onSubmit) {
      onSubmit(payload);
      return;
    }
    // SE-4 — wire to the slip-extract Edge fn via useSlipExtraction.
    await runExtract({
      input_mode: payload.input_mode,
      slip_text: payload.slip_text,
      slip_image_path: payload.slip_image_path,
      slip_image_mime: payload.slip_image_mime,
      slip_image_size_bytes: payload.slip_image_size_bytes,
      user_prompt: payload.user_prompt,
      analysis: payload.analysis,
      stake: payload.stake,
      slips_per_week: payload.slips_per_week,
    });
  }

  // ─── State-machine takeover (spec §7 SE-4 done criteria) ─────
  if (extractionState === "extracting") {
    return (
      <ProgressCard
        testid="sle-extracting"
        eyebrow="STRIKZA is reading your slip"
        body={mode === "upload"
          ? "Vision agent is extracting each leg from your image…"
          : "Parsing each leg from your pasted text…"}
      />
    );
  }

  if (extractionState === "queued") {
    // N01 graceful path — vendor errors land here.
    return (
      <div
        data-testid="sle-queued"
        style={{
          padding: "20px 18px",
          border: `1px solid ${SE.amber}`,
          borderLeft: `2px solid ${SE.amber}`,
          borderRadius: 4,
          background: SE.surfaceCard,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontFamily: SE.fontMono,
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: SE.amber,
          }}
        >
          Analysis queued
        </div>
        <div
          style={{
            fontFamily: SE.fontBody,
            fontSize: 13.5,
            color: SE.textSec,
            lineHeight: 1.55,
          }}
        >
          We&rsquo;re working on it — you&rsquo;ll be notified when the
          extraction is complete. You can also try running it again.
        </div>
        <button
          type="button"
          onClick={resetExtraction}
          data-testid="sle-queued-retry"
          style={{
            alignSelf: "flex-start",
            padding: "9px 16px",
            background: "transparent",
            color: SE.textSec,
            border: `1px solid ${SE.border}`,
            borderRadius: 2,
            fontFamily: SE.fontBody,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  // SE-6 takeover branches — scoring / planning / awaiting_confirmation
  // / declined. SE-7 fills the executing/complete branches.
  if (extractionState === "scoring") {
    return (
      <ProgressCard
        testid="sle-scoring"
        eyebrow="Scoring legs"
        body="Scoring every leg against Fair Price, CLV, DNA, and your history…"
      />
    );
  }

  if (extractionState === "planning") {
    return (
      <ProgressCard
        testid="sle-planning"
        eyebrow="Building plan"
        body="Translating your request into a structured plan…"
      />
    );
  }

  if (extractionState === "awaiting_confirmation" && filterPlan) {
    return (
      <PlanCard
        plan={filterPlan}
        onRun={() => void runPlan()}
        onCancel={cancelPlan}
      />
    );
  }

  // SE-7 — declined state: prefer the executor result if it has flowed
  // through (executor surfaces decline_reason verbatim); else fall back
  // to the PlanCard's decline branch from SE-6.
  if (extractionState === "declined") {
    if (filterResult) {
      return <ResultPanel result={filterResult} liabilities={liabilities} analysisDegraded={analysisDegraded} degradedLegCount={degradedLegCount} onReset={resetExtraction} />;
    }
    if (filterPlan) {
      return (
        <PlanCard
          plan={filterPlan}
          onRun={() => void runPlan()}
          onCancel={cancelPlan}
        />
      );
    }
  }

  if (extractionState === "executing") {
    return (
      <ProgressCard testid="sle-executing" eyebrow="Applying plan…" />
    );
  }

  if (extractionState === "complete" && filterResult) {
    // T3.1 — source combined odds = the pre-trim slip (every scored
    // leg, kept + dropped). The card itself is built from PUBLIC
    // fields only inside ResultPanel (N15); nothing internal leaks.
    const allLegs = [
      ...filterResult.kept,
      ...filterResult.dropped.map((d) => d.leg),
    ];
    const breakEven = financialContext && filterResult.combined_odds_kept
      ? computeBreakEvenAnchor({
        stake: financialContext.stake,
        slipsPerWeek: financialContext.slipsPerWeek,
        combinedOdds: filterResult.combined_odds_kept,
      })
      : null;
    return (
      <ResultPanel
        result={filterResult}
        liabilities={liabilities}
        analysisDegraded={analysisDegraded}
        degradedLegCount={degradedLegCount}
        stressReport={stressReport}
        breakEvenStatement={breakEven?.statement ?? null}
        onReset={resetExtraction}
        shareContext={{ sourceCombinedOdds: computeCombinedOdds(allLegs) }}
      />
    );
  }

  if (extractionState === "extracted" && extractionResult) {
    // SE-4 debug surface + SE-6 hand-off. Legs are EDITABLE here so the user
    // can fix any OCR misread (vision transcribes verbatim, but compressed
    // phone screenshots aren't perfect) before scoring.
    const legs: any[] = editedLegs ?? extractionResult.extracted_legs;
    const setField = (i: number, field: string, value: unknown): void => {
      setEditedLegs((prev) => {
        const base = prev ?? extractionResult.extracted_legs.map((l) => ({ ...l }));
        return base.map((l, idx) => (idx === i ? { ...l, [field]: value } : l));
      });
    };
    const removeLeg = (i: number): void => {
      setEditedLegs((prev) => {
        const base = prev ?? extractionResult.extracted_legs.map((l) => ({ ...l }));
        return base.filter((_, idx) => idx !== i);
      });
    };
    const cellInput: React.CSSProperties = {
      width: "100%",
      boxSizing: "border-box",
      background: SE.surfacePage,
      border: `1px solid ${SE.border}`,
      borderRadius: 2,
      color: SE.text,
      fontFamily: SE.fontMono,
      fontSize: 12,
      padding: "5px 7px",
      outline: "none",
    };
    const liveCombined = legs.reduce(
      (acc, l) => (typeof l.odds === "number" && l.odds > 0 ? acc * l.odds : acc),
      1,
    );
    return (
      <div
        data-testid="sle-extracted"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "20px 18px",
          border: `1px solid ${SE.border}`,
          borderLeft: `2px solid ${SE.keepGreen}`,
          borderRadius: 4,
          background: SE.surfaceRaised,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontFamily: SE.fontMono,
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: SE.keepGreen,
            }}
          >
            {legs.length} legs extracted · edit any misread
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={resetExtraction}
              data-testid="sle-extracted-reset"
              style={{
                padding: "9px 16px",
                background: "transparent",
                color: SE.textSec,
                border: `1px solid ${SE.border}`,
                borderRadius: 2,
                fontFamily: SE.fontBody,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              New extraction
            </button>
            <button
              type="button"
              onClick={() => void scoreAndPlan(legs)}
              disabled={legs.length === 0}
              data-testid="sle-extracted-score-and-plan"
              style={{
                padding: "9px 18px",
                background: legs.length === 0 ? SE.surfaceRaised : SE.amber,
                color: legs.length === 0 ? SE.textMuted : SE.surfacePage,
                border: "none",
                borderRadius: 2,
                fontFamily: SE.fontBody,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: legs.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              Score &amp; plan
            </button>
          </div>
        </div>
        <div
          style={{
            fontFamily: SE.fontBody,
            fontSize: 12,
            color: SE.textMuted,
            lineHeight: 1.5,
          }}
        >
          Names are transcribed straight from your slip. Fix any the photo
          made unclear, then Score &amp; plan.
        </div>
        {legs.length === 0 ? (
          <div
            style={{
              fontFamily: SE.fontBody,
              fontSize: 13.5,
              color: SE.textSec,
              lineHeight: 1.55,
            }}
          >
            No legs left. Add some via a clearer image, or paste the legs as text.
          </div>
        ) : (
          <table
            data-testid="sle-extracted-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: SE.fontMono,
              fontSize: 12,
            }}
          >
            <thead>
              <tr style={{ textAlign: "left", color: SE.textMuted }}>
                <th style={{ padding: "6px 6px", width: 22 }}>#</th>
                <th style={{ padding: "6px 6px", width: 92 }}>Sport</th>
                <th style={{ padding: "6px 6px" }}>Home</th>
                <th style={{ padding: "6px 6px" }}>Away</th>
                <th style={{ padding: "6px 6px" }}>Market</th>
                <th style={{ padding: "6px 6px" }}>Selection</th>
                <th style={{ padding: "6px 6px", width: 72 }}>Odds</th>
                <th style={{ padding: "6px 6px", width: 28 }} />
              </tr>
            </thead>
            <tbody>
              {legs.map((leg, i) => (
                <tr
                  key={i}
                  data-testid={`sle-leg-${i}`}
                  style={{ borderTop: `1px solid ${SE.border}`, color: SE.text }}
                >
                  <td style={{ padding: "4px 6px", color: SE.textMuted }}>{i + 1}</td>
                  <td style={{ padding: "4px 6px" }}>
                    <select
                      value={(leg.sport as string) ?? "football"}
                      onChange={(e) => setField(i, "sport", e.target.value)}
                      data-testid={`sle-leg-${i}-sport`}
                      style={{ ...cellInput, cursor: "pointer", padding: "5px 4px" }}
                    >
                      <option value="football">Football</option>
                      <option value="tennis">Tennis</option>
                      <option value="basketball">Basketball</option>
                      <option value="other">Other</option>
                    </select>
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input
                      value={leg.home_team ?? ""}
                      onChange={(e) => setField(i, "home_team", e.target.value)}
                      data-testid={`sle-leg-${i}-home`}
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input
                      value={leg.away_team ?? ""}
                      onChange={(e) => setField(i, "away_team", e.target.value)}
                      data-testid={`sle-leg-${i}-away`}
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input
                      value={leg.market ?? ""}
                      onChange={(e) => setField(i, "market", e.target.value)}
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input
                      value={leg.selection ?? ""}
                      onChange={(e) => setField(i, "selection", e.target.value)}
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      value={typeof leg.odds === "number" ? leg.odds : ""}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setField(i, "odds", Number.isFinite(v) ? v : null);
                      }}
                      style={{ ...cellInput, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "4px 6px", textAlign: "center" }}>
                    <button
                      type="button"
                      onClick={() => removeLeg(i)}
                      aria-label={`Remove leg ${i + 1}`}
                      data-testid={`sle-leg-${i}-remove`}
                      title="Remove this leg"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: SE.textMuted,
                        cursor: "pointer",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: 2,
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {legs.length > 0 && liveCombined > 1 && (
          <div
            data-testid="sle-extracted-combined-odds"
            style={{
              fontFamily: SE.fontMono,
              fontSize: 11,
              letterSpacing: "0.12em",
              color: SE.textSec,
              alignSelf: "flex-end",
            }}
          >
            Combined odds: {liveCombined.toFixed(2)}
          </div>
        )}
      </div>
    );
  }

  // ─── Default: the Brief (design's numbered section stack) ────
  const tabBtn = (selected: boolean): React.CSSProperties => ({
    padding: "8px 16px",
    background: selected ? SE.surfaceCard : "transparent",
    color: selected ? SE.text : SE.textSec,
    border: `1px solid ${selected ? SE.borderBright : SE.border}`,
    borderRadius: 2,
    fontFamily: SE.fontBody,
    fontSize: 13,
    cursor: "pointer",
  });

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="sle-form"
      data-mode={mode}
      style={{ display: "flex", flexDirection: "column", gap: 28 }}
    >
      {/* 01 · Intent */}
      <section>
        <SectionHead
          number="01"
          eyebrow="Your intent"
          sub="Tell the system what you want out of this slip."
        />
        <PromptComposer
          value={prompt}
          onChange={setPrompt}
          disabled={submitting}
        />
      </section>

      {/* 02 · The slip */}
      <section>
        <SectionHead
          number="02"
          eyebrow="The slip"
          sub="Upload a slip from social media (X, TikTok, WhatsApp) or paste it."
        />
        <div
          style={{ display: "flex", gap: 8, marginBottom: 14 }}
          role="tablist"
          aria-label="Slip input mode"
        >
          <button
            type="button"
            onClick={() => setMode("upload")}
            data-testid="sle-mode-upload"
            data-active={mode === "upload" || undefined}
            role="tab"
            aria-selected={mode === "upload"}
            style={tabBtn(mode === "upload")}
          >
            Upload slip
          </button>
          <button
            type="button"
            onClick={() => setMode("paste")}
            data-testid="sle-mode-paste"
            data-active={mode === "paste" || undefined}
            role="tab"
            aria-selected={mode === "paste"}
            style={tabBtn(mode === "paste")}
          >
            Paste text
          </button>
        </div>

        {mode === "upload" ? (
          user ? (
            // The dropzone's CSS lives under `.sa-root .slip-extractor-2`
            // (descendant combinator — .sa-root must be an ANCESTOR of
            // .slip-extractor-2; it also defines the --sa-* color vars).
            // The extractor page roots on `.se-root`, so we re-create the
            // expected ancestor chain here. Nested (not both classes on one
            // div) — otherwise the descendant selectors, incl. the 96px
            // `.sa-upload-thumb` cap, never match and the preview image
            // renders at full natural size.
            <div className="sa-root">
              <div className="slip-extractor-2">
                <SlipUploadDropzone
                  userId={user.id}
                  value={upload}
                  onUploadComplete={setUpload}
                  onClear={() => setUpload(null)}
                  disabled={submitting}
                />
              </div>
            </div>
          ) : (
            <div
              data-testid="sle-upload-signin-hint"
              style={{
                padding: "28px 20px",
                border: `1px dashed ${SE.border}`,
                borderRadius: 3,
                textAlign: "center",
                fontFamily: SE.fontBody,
                fontSize: 13.5,
                color: SE.textSec,
              }}
            >
              Sign in to upload a slip image.
            </div>
          )
        ) : (
          <textarea
            id="sle-paste-textarea"
            data-testid="sle-paste-textarea"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            disabled={submitting}
            rows={6}
            placeholder={
              "Paste the slip text from a tipster post:\n\nBrighton vs Bournemouth — Match Winner — Home @ 1.72\nBayern vs RB Leipzig — BTTS — Yes @ 2.00\n…"
            }
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "16px 18px",
              background: SE.surfacePage,
              border: `1px solid ${SE.border}`,
              borderRadius: 2,
              color: SE.text,
              fontFamily: SE.fontBody,
              fontSize: 14,
              lineHeight: 1.55,
              resize: "vertical",
              minHeight: 120,
              outline: "none",
            }}
          />
        )}

        {/* T3.2 — analysis-mode selector. Stress-test is Sika Pro+;
            locked (not hidden) for lower tiers so the upgrade
            incentive is visible and honest. */}
        <div
          role="tablist"
          aria-label="Analysis mode"
          data-testid="sle-analysis-toggle"
          style={{ display: "flex", gap: 8, marginTop: 14 }}
        >
          <button
            type="button"
            onClick={() => setAnalysis("optimise")}
            data-testid="sle-analysis-optimise"
            data-active={analysis === "optimise" || undefined}
            role="tab"
            aria-selected={analysis === "optimise"}
            style={tabBtn(analysis === "optimise")}
          >
            Optimise
          </button>
          <button
            type="button"
            onClick={() => canStressTest && setAnalysis("stress_test")}
            disabled={!canStressTest}
            title={canStressTest
              ? "Find your slip's weakest link"
              : "Stress-test is a Sika Pro feature"}
            data-testid="sle-analysis-stress"
            data-active={analysis === "stress_test" || undefined}
            role="tab"
            aria-selected={analysis === "stress_test"}
            style={{
              ...tabBtn(analysis === "stress_test"),
              opacity: canStressTest ? 1 : 0.55,
              cursor: canStressTest ? "pointer" : "not-allowed",
            }}
          >
            Stress-test{canStressTest ? "" : " · Sika Pro"}
          </button>
        </div>
      </section>

      {/* 03 · Stake context (optional) */}
      <section>
        <SectionHead
          number="03"
          eyebrow="Stake context · optional"
          sub="Powers the break-even anchor at session seal. Leave blank if you'd rather not declare a stake — the anchor will simply not render. Never used to predict outcomes."
        />
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <StakeField
            label="Stake per slip"
            unit="GH₵"
            value={stake}
            onChange={setStake}
            options={STAKE_OPTIONS}
            testid="sle-stake-per-slip"
          />
          <StakeField
            label="Slips per week"
            unit="×"
            value={slipsPerWeek}
            onChange={setSlipsPerWeek}
            options={SPW_OPTIONS}
            testid="sle-stake-slips-per-week"
          />
        </div>
        {!(stake && slipsPerWeek) && (
          <div
            data-testid="sle-stake-anchor-placeholder"
            style={{
              marginTop: 14,
              padding: "14px 18px",
              border: `1px dashed ${SE.border}`,
              borderRadius: 3,
              fontFamily: SE.fontMono,
              fontSize: 10.5,
              letterSpacing: "0.04em",
              color: SE.textMuted,
            }}
          >
            Declare a stake and weekly cadence to render the break-even
            anchor. Both are optional. N08 · honest unavailability.
          </div>
        )}
      </section>

      {/* Input validation hint (state==='error_input') */}
      {extractionState === "error_input" && extractionInputError && (
        <div
          data-testid="sle-form-input-error"
          style={{
            padding: "10px 12px",
            border: `1px solid ${SE.amber}`,
            borderLeft: `2px solid ${SE.amber}`,
            borderRadius: 4,
            background: SE.surfaceCard,
            fontFamily: SE.fontBody,
            fontSize: 13,
            color: SE.text,
          }}
        >
          {extractionInputError}
        </div>
      )}

      {/* CTA bar — readiness sentence + "Open the session" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          borderTop: `1px solid ${SE.border}`,
          paddingTop: 18,
        }}
      >
        <span
          data-testid="sle-prompt-readiness"
          style={{
            fontFamily: SE.fontMono,
            fontSize: 11,
            letterSpacing: "0.06em",
            color: SE.textMuted,
          }}
        >
          {submitReady ? (
            <>
              Intent locked · slip captured
              {stake && slipsPerWeek
                ? " · stake set"
                : " · stake not declared"}.
            </>
          ) : !slipReady ? (
            <span data-testid="sle-submit-hint-slip">
              Still missing:{" "}
              <span style={{ color: SE.text }}>a slip to negotiate</span>.
            </span>
          ) : (
            <span data-testid="sle-submit-hint-prompt">
              Still missing:{" "}
              <span style={{ color: SE.text }}>a clear intent line</span>.
            </span>
          )}
        </span>
        <button
          type="submit"
          disabled={!submitReady}
          data-testid="sle-form-submit"
          style={{
            padding: "11px 24px",
            border: "none",
            borderRadius: 2,
            background: submitReady ? SE.amber : SE.surfaceRaised,
            color: submitReady ? SE.surfacePage : SE.textMuted,
            fontFamily: SE.fontBody,
            fontSize: 13.5,
            fontWeight: 600,
            letterSpacing: "0.01em",
            cursor: submitReady ? "pointer" : "not-allowed",
          }}
        >
          Open the session
        </button>
      </div>
    </form>
  );
}
